import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useListModelsQuery,
  useListToolsQuery,
  useGetParameterCatalogueQuery,
  AgentVersion,
} from '@/redux/internal-services/agentsApi';

import {
  AgentFormValues,
  agentSchema,
  emptyAgent,
  AgentTypeBadge,
  Modal,
  SNAP_FIELDS,
  snapshot,
  makeVersion,
} from '@/components/Agent/agentShared';
import OverviewTab from '@/components/Agent/OverviewTab';
import InstructionsTab from '@/components/Agent/InstructionsTab';
import ToolsTab from '@/components/Agent/ToolsTab';
import WorkflowTab from '@/components/Agent/WorkflowTab';
import { VersionsTab, SaveVersionModal, ViewVersionModal } from '@/components/Agent/VersionsTab';
import TestPanel from '@/components/Agent/TestPanel';

// ── types ──────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'instructions' | 'tools' | 'advanced' | 'workflow' | 'test' | 'versions';
type ModalState =
  | null
  | { kind: 'saveVersion' }
  | { kind: 'viewVersion'; version: AgentVersion }
  | { kind: 'promptPreview' };

// ── AgentConfigPage ────────────────────────────────────────────────────────────

const AgentConfigPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId && agentId !== 'new');
  const navigate = useNavigate();

  const { data: existing } = useGetAgentQuery(agentId as string, { skip: !isEdit });
  const { data: models } = useListModelsQuery();
  const { data: allTools } = useListToolsQuery();
  const { data: paramCat } = useGetParameterCatalogueQuery();
  const [createAgent, { isLoading: creating }] = useCreateAgentMutation();
  const [updateAgent, { isLoading: updating }] = useUpdateAgentMutation();

  const [tab, setTab] = useState<Tab>('overview');
  const [modal, setModal] = useState<ModalState>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [serverError, setServerError] = useState('');
  const [previewText, setPreviewText] = useState<string | null>(null);

  // ── form ────────────────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const methods = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema) as any,
    defaultValues: emptyAgent,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isDirty, errors },
  } = methods;

  // Populate form when server data loads
  useEffect(() => {
    if (existing) reset({ ...emptyAgent, ...existing });
  }, [existing, reset]);

  // Seed first model when creating a new agent
  useEffect(() => {
    if (!isEdit && models && models.length > 0 && !getValues('models.text')) {
      setValue('models.text', models[0].name, { shouldDirty: false });
    }
  }, [models, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived state ────────────────────────────────────────────────────────────

  const agentType = watch('agent_type');
  const agentModelText = watch('models.text');
  const agentVersions = watch('versions') || [];
  const publishedId = watch('published_version_id');
  const isWorkflow = agentType === 'workflow';

  const sortedVersions = useMemo(
    () => [...agentVersions].sort((a, b) => b.n - a.n),
    [agentVersions],
  );
  const latestVersion = agentVersions.length
    ? agentVersions[agentVersions.length - 1]
    : null;
  const publishedVersion = agentVersions.find((v) => v.id === publishedId) || null;

  const dirtyVsLatest = latestVersion
    ? JSON.stringify(snapshot(getValues() as Record<string, unknown>)) !== JSON.stringify(latestVersion.snapshot)
    : (watch('system_prompts') || []).length > 0;

  const paramGroups = paramCat?.groups || {};

  const tabs: Array<{ k: Tab; l: string }> = [
    { k: 'overview', l: 'Overview' },
    { k: 'instructions', l: 'Instructions' },
    { k: 'tools', l: 'Tools' },
    { k: 'advanced', l: 'Advanced' },
    ...(isEdit && isWorkflow ? [{ k: 'workflow' as Tab, l: 'Workflow' }] : []),
    ...(isEdit ? [{ k: 'test' as Tab, l: 'Test' }, { k: 'versions' as Tab, l: 'History' }] : []),
  ];

  // ── actions ──────────────────────────────────────────────────────────────────

  const persist = async (values: AgentFormValues): Promise<boolean> => {
    setServerError('');
    // Stamp order from array index so backend sort matches the UI order.
    const payload: AgentFormValues = {
      ...values,
      system_prompts: (values.system_prompts || []).map((b, i) => ({ ...b, order: i })),
    };
    try {
      if (isEdit) {
        await updateAgent({ id: agentId as string, body: payload }).unwrap();
      } else {
        await createAgent(payload).unwrap();
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
      return true;
    } catch (err: unknown) {
      setServerError(getErrorDetail(err) ?? 'Failed to save agent.');
      return false;
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    const ok = await persist(values as AgentFormValues);
    if (ok && !isEdit) navigate('/platform/agents');
  });

  const handlePreview = () => {
    const prompts = getValues('system_prompts') || [];
    if (prompts.length === 0) {
      setPreviewText('(no prompt blocks defined)');
    } else {
      const assembled = prompts
        .map((block) => {
          const tag = block.category || 'prompt';
          const attrs = [
            block.title ? ` title="${block.title}"` : '',
            block.condition_roles && block.condition_roles.length > 0
              ? ` roles="${block.condition_roles.join(', ')}"`
              : '',
          ].join('');
          return `<${tag}${attrs}>\n${block.prompt || ''}\n</${tag}>`;
        })
        .join('\n\n');
      setPreviewText(assembled);
    }
    setModal({ kind: 'promptPreview' });
  };

  const exportAgent = () => {
    const values = getValues();
    const blob = new Blob([JSON.stringify(values, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(values.agent_name || 'agent').replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importAgent = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = JSON.parse(await file.text());
      reset({ ...emptyAgent, ...imported });
      setServerError('');
    } catch {
      setServerError('Import failed — file is not valid agent JSON.');
    }
    e.target.value = '';
  };

  // ── version management ────────────────────────────────────────────────────────

  const saveVersion = async (message: string, publish: boolean) => {
    const v = makeVersion(getValues() as Record<string, unknown>, message);
    const currentVersions = getValues('versions') || [];
    const nextVersions = [...currentVersions, v];
    setValue('versions', nextVersions, { shouldDirty: true });
    if (publish) setValue('published_version_id', v.id, { shouldDirty: true });
    setModal(null);
    if (isEdit) await methods.handleSubmit((v) => persist(v as AgentFormValues))();
  };

  const restoreVersion = (v: AgentVersion) => {
    const snap = v.snapshot as Partial<AgentFormValues>;
    SNAP_FIELDS.forEach((k) => {
      if (k in snap) setValue(k as keyof AgentFormValues, snap[k as keyof typeof snap] as never, { shouldDirty: true });
    });
    setModal(null);
    setTab('instructions');
  };

  const publishVersion = async (v: AgentVersion) => {
    setValue('published_version_id', v.id, { shouldDirty: true });
    setModal(null);
    if (isEdit) await methods.handleSubmit((v) => persist(v as AgentFormValues))();
  };

  // ── render ───────────────────────────────────────────────────────────────────

  const agentName = watch('agent_name');
  const nextVersionN = agentVersions.length
    ? Math.max(...agentVersions.map((v) => v.n)) + 1
    : 1;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} noValidate className="h-full flex flex-col">

        {/* ── Always-visible header (breadcrumb + title + actions + tabs) ── */}
        <div className="bg-ui-card border-b border-[#EDEDED] dark:border-white/10 shadow-sm shrink-0">
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* top row: back / title / buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-3 pb-2">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => navigate('/platform/agents')}
                  className="text-xs text-ui-text3 hover:text-gray-600 mb-0.5"
                >
                  ← Agents
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg font-bold text-ui-text truncate">
                    {isEdit ? (agentName || 'Edit Agent') : 'New Agent'}
                  </h1>
                  <AgentTypeBadge type={agentType} />
                </div>
                <p className="text-xs text-ui-text3">
                  <span className="font-mono">{agentModelText}</span>
                  {publishedVersion ? (
                    <> · <span className="text-emerald-600">v{publishedVersion.n} live</span></>
                  ) : isEdit ? (
                    ' · no published version'
                  ) : null}
                  {isEdit && dirtyVsLatest ? (
                    <span className="text-amber-600"> · unsaved version</span>
                  ) : null}
                </p>
              </div>

              {savedFlash && <span className="text-xs text-emerald-600 font-medium">✓ Saved</span>}
              {isDirty && !savedFlash && (
                <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
              )}

              <button
                type="button"
                onClick={exportAgent}
                className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover transition"
              >
                Export
              </button>
              <label className="border border-[#EDEDED] dark:border-white/10 text-ui-text px-3 py-1.5 rounded-lg text-sm hover:bg-ui-hover transition cursor-pointer">
                Import
                <input type="file" accept="application/json" className="hidden" onChange={importAgent} />
              </label>

              <button
                type="submit"
                disabled={creating || updating}
                className="bg-brand-dark text-white px-5 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-dark/90 transition disabled:opacity-50"
              >
                {creating || updating ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Agent'}
              </button>
            </div>

            {/* tab row */}
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setTab(t.k)}
                  className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors ${
                    tab === t.k
                      ? 'border-brand-dark font-medium text-brand-dark'
                      : 'border-transparent text-ui-text2 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Validation / server errors */}
          {(serverError || errors.agent_name || errors.models?.text) && (
            <div className="mb-4 rounded-lg border border-[#CF4148]/20 bg-red-50 px-4 py-3 text-sm text-[#CF4148]">
              {serverError ||
                errors.agent_name?.message ||
                errors.models?.text?.message}
            </div>
          )}

          {tab === 'overview' && (
            <OverviewTab
              isEdit={isEdit}
              models={models || []}
              paramGroups={paramGroups}
            />
          )}

          {tab === 'instructions' && (
            <InstructionsTab
              isEdit={isEdit}
              onPreview={handlePreview}
              previewing={false}
            />
          )}

          {tab === 'tools' && <ToolsTab allTools={allTools || []} />}

          {tab === 'advanced' && (
            <AdvancedTab paramGroups={paramGroups} />
          )}

          {tab === 'workflow' && isEdit && isWorkflow && (
            <WorkflowTab agentId={agentId as string} triggerType={watch('trigger_type')} />
          )}

          {tab === 'test' && isEdit && (
            <TestPanel agentId={agentId as string} agentName={agentName} />
          )}

          {tab === 'versions' && isEdit && (
            <VersionsTab
              versions={sortedVersions}
              publishedId={publishedId}
              dirty={dirtyVsLatest}
              latestN={latestVersion?.n}
              onSaveVersion={() => setModal({ kind: 'saveVersion' })}
              onView={(v) => setModal({ kind: 'viewVersion', version: v })}
            />
          )}
          </div>
        </div>

        {/* ── Modals ── */}
        {modal?.kind === 'saveVersion' && (
          <SaveVersionModal
            nextN={nextVersionN}
            onClose={() => setModal(null)}
            onSave={saveVersion}
          />
        )}
        {modal?.kind === 'viewVersion' && (
          <ViewVersionModal
            version={modal.version}
            current={getValues()}
            isPublished={modal.version.id === publishedId}
            onClose={() => setModal(null)}
            onRestore={() => restoreVersion(modal.version)}
            onPublish={() => publishVersion(modal.version)}
          />
        )}
        {modal?.kind === 'promptPreview' && previewText !== null && (
          <Modal wide title="Compiled system prompt (preview)" onClose={() => setModal(null)}>
            <p className="mb-3 text-xs text-ui-text3">
              Current prompt blocks assembled in order. Variables shown as tokens — substituted at runtime.
            </p>
            <pre className="whitespace-pre-wrap font-mono text-[12px] leading-relaxed bg-ui-raised border border-[#EDEDED] dark:border-white/10 rounded-lg p-3 max-h-[50vh] overflow-y-auto text-ui-text">
              {previewText}
            </pre>
          </Modal>
        )}
      </form>
    </FormProvider>
  );
};

// ── AdvancedTab (inline — only 2 static cards) ─────────────────────────────────

import { useFormContext } from 'react-hook-form';
import { DescriptorField, SectionCard } from '@/components/Agent/agentShared';
import { ParameterDescriptor } from '@/redux/internal-services/agentsApi';

const AdvancedTab: React.FC<{ paramGroups: Record<string, ParameterDescriptor[]> }> = ({ paramGroups }) => {
  const { watch, setValue } = useFormContext<AgentFormValues>();

  const modelsParams = watch('models') as Record<string, unknown> || {};
  const voiceParams = watch('voice') || {};

  const setModelField = (key: string, value: unknown) =>
    setValue('models', { ...modelsParams, [key]: value } as AgentFormValues['models'], { shouldDirty: true });

  const setVoiceField = (key: string, value: unknown) =>
    setValue('voice', { ...voiceParams, [key]: value }, { shouldDirty: true });

  const modelDescriptors = paramGroups['models'] || [];
  const voiceDescriptors = paramGroups['voice'] || [];

  return (
    <>
      <SectionCard
        title="Models"
        desc="Realtime model override for interactive/hands-free mode. Leave blank to use the platform default."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {modelDescriptors.map((d) => (
            <DescriptorField
              key={d.key}
              d={d}
              value={modelsParams[d.key] ?? d.default}
              onChange={(v) => setModelField(d.key, v)}
            />
          ))}
          {modelDescriptors.length === 0 && (
            <p className="text-xs text-ui-text3">No model overrides defined.</p>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Voice / STT / TTS">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {voiceDescriptors.map((d) => (
            <DescriptorField
              key={d.key}
              d={d}
              value={voiceParams[d.key] ?? d.default}
              onChange={(v) => setVoiceField(d.key, v)}
            />
          ))}
          {voiceDescriptors.length === 0 && (
            <p className="text-xs text-ui-text3">No voice parameters defined.</p>
          )}
        </div>
      </SectionCard>
    </>
  );
};

export default AgentConfigPage;
